<template>
   <header
      id="header"
      :class="{
         'search-results-page': route?.name == 'search-results',
      }"
      ref="el"
   >
      <!-- Bar -->
      <div class="nav-bar" role="menubar">
         <nuxt-link to="/" :aria-label="$config.public.siteName" class="logo"
            ><LogoIcon
         /></nuxt-link>
         <SearchForm
            v-if="
               route?.name !== 'search-results' &&
               authed &&
               ![404, 500].includes(error?.statusCode)
            "
            placeholder="Search for courses, lessons, and more"
            key="instant-search-header"
         />
         <nav role="navigation">
            <ul class="main-nav">
               <template v-if="authed && user">
                  <li v-for="item in disciplines" :key="item">
                     <nuxt-link
                        :to="`/discipline/${disciplineText(item)}/explore`"
                        >{{ disciplineText(item) }}</nuxt-link
                     >
                  </li>

                  <li><nuxt-link to="/pd">PD Courses</nuxt-link></li>
                  <li>
                     <nuxt-link to="/help-center">Help Center</nuxt-link>
                  </li>

                  <li
                     class="has-children"
                     @mouseenter="profileMenuOpen = true"
                     @mouseleave="profileMenuOpen = false"
                  >
                     <nuxt-link
                        :to="{
                           name: 'profile-classes',
                           path: `/profile/classes`,
                        }"
                     >
                        <LazyAvatar />
                     </nuxt-link>
                     <transition
                        @enter="handleDDAnim"
                        @leave="handleDDAnim"
                        :css="false"
                     >
                        <div
                           :class="['profile-dd', { open: profileMenuOpen }]"
                           v-show="profileMenuOpen"
                        >
                           <div :class="['profile-menu']">
                              <nuxt-link
                                 :to="{
                                    name: 'profile-classes',
                                    path: `/profile/classes`,
                                 }"
                              >
                                 Profile
                              </nuxt-link>
                              <nuxt-link
                                 :to="{
                                    name: 'profile-settings-general',
                                 }"
                              >
                                 Settings
                              </nuxt-link>
                              <button @click="handleExportData">
                                 Export Data
                              </button>
                              <button @click="CookieConsent.showPreferences()">
                                 Manage Cookie Preferences
                              </button>
                              <button class="logout-btn" @click="handleLogout">
                                 Log out
                              </button>
                           </div>
                        </div>
                     </transition>
                  </li>
               </template>
               <template v-else>
                  <li>
                     <nuxt-link :to="{ name: 'login-modal', path: `/login` }"
                        >Log in</nuxt-link
                     >
                  </li>
               </template>
            </ul>
         </nav>
      </div>
   </header>
</template>

<script setup lang="ts">
import LogoIcon from "~/assets/images/logo.svg?component";
const error = useError();
const CookieConsent = useNuxtApp().$CookieConsent;
const { scrollState } = useLenis();
const { addDynamicEl } = useDynamicHeight();
const { getExportData } = useProfile();
const { logError } = useDebugger();
const { track } = useMixpanel();
const { $posthog } = useNuxtApp();
const authedStore = useAuthStore();
const { authed, user, getUserDisciplines } = storeToRefs(authedStore);
const el = useTemplateRef("el");
const gsap = import.meta.client ? useNuxtApp().$gsap : null;
const profileMenuOpen = ref(false);
const scrolling = ref(0);
const route = useRoute();
// const disciplines = reactive(["Music", "Drama", "Dance"]);
// >> Computed ------------------------------------------------------
const disciplines = computed(() => {
   const allDisciplines = getUserDisciplines.value;
   const userDiscipline = user.value?.discipline;

   const index = allDisciplines?.indexOf(userDiscipline);
   if (index > 0) {
      // Remove the user's discipline and place it at the beginning.
      const filtered = allDisciplines.filter((d) => d !== userDiscipline);
      return [userDiscipline, ...filtered];
   }
   return allDisciplines;
});
// >> Handlers ------------------------------------------------------
const handleExportData = async () => {
   try {
      const { pdf } = await getExportData();
      window.open(pdf, "_blank");
      track("$exported_user_data", {
         $user: user.value?.email,
      });
      $posthog().capture("exported_user_data");
   } catch (err) {
      logError("Export data failed", err);
   }
};
const handleLogout = async () => {
   await authedStore.logout();
   await navigateTo({
      path: "/",
   });
};
// >> Animations ------------------------------------------------------

const handleDDAnim = (el, done) => {
   gsap.to(el, {
      height: profileMenuOpen.value ? `auto` : `0px`,
      duration: 0.2,
      ease: "cubic.inOut",
      overwrite: true,
      onComplete: done,
   });
};
//>> WATCHERS -------------------------
watch(
   scrollState,
   ({ scroll, direction, limit }) => {
      if (scroll > 0) {
         direction == -1 ? (scrolling.value = false) : (scrolling.value = true);
      } else {
         scrolling.value = false;
      }
   },
   { deep: true },
);
//>> OBSERVERS -------------------------
// >> LIFE CYCLES ------------------------------------------------------
onMounted(() => {
   addDynamicEl("header-height", el.value);
});
</script>
<style lang="scss">
@import url("~/assets/scss/header.scss");
</style>
